// src/utils/userType.js
//
// Shared "student" vs "business" mode, readable/writable from any component
// (Header, GatewayModal, Switch menu, etc.) without prop drilling or context
// setup. Backed by localStorage for persistence across reloads, and a
// same-tab CustomEvent so already-mounted components (like Header, which
// never unmounts between route changes) update immediately when something
// else changes the mode. A native `storage` event handles cross-tab sync.

import { useState, useEffect, useCallback } from "react";

export const STORAGE_KEY = "netlabs_user_type";
const EVENT_NAME = "netlabs:user-type-change";
const VALID_TYPES = ["student", "business"];
const DEFAULT_TYPE = "student";

export function getUserType() {
  if (typeof window === "undefined") return DEFAULT_TYPE;
  const stored = window.localStorage.getItem(STORAGE_KEY);
  return VALID_TYPES.includes(stored) ? stored : DEFAULT_TYPE;
}

export function setUserType(value) {
  if (!VALID_TYPES.includes(value)) return;
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STORAGE_KEY, value);
  // Notify listeners in the SAME tab (storage events only fire in other tabs)
  window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: value }));
}

/**
 * React hook that stays in sync with the current mode, no matter which
 * component (or which tab) changed it.
 */
export function useUserType() {
  const [userType, setUserTypeState] = useState(getUserType);

  useEffect(() => {
    const handleCustomChange = (e) => setUserTypeState(e.detail);
    const handleStorageChange = (e) => {
      if (e.key === STORAGE_KEY) {
        setUserTypeState(VALID_TYPES.includes(e.newValue) ? e.newValue : DEFAULT_TYPE);
      }
    };

    window.addEventListener(EVENT_NAME, handleCustomChange);
    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener(EVENT_NAME, handleCustomChange);
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  const updateUserType = useCallback((value) => setUserType(value), []);

  return [userType, updateUserType];
}